/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Field from '@atlaskit/form/field';
import Form from '@atlaskit/form/form';
import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import CrossIcon from '@atlaskit/icon/core/cross';
import DeleteIcon from '@atlaskit/icon/core/delete';
import EditIcon from '@atlaskit/icon/core/edit';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import ButtonItem from '@atlaskit/menu/button-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import { Popup } from '@atlaskit/popup/popup';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import defaultMessages from './messages';
import type { ChatListItemProps } from './types';

const POPUP_Z_INDEX = 600;

const styles = cssMap({
	renameForm: {
		display: 'flex',
		border: `${token('border.width')} solid transparent`,
	},
	innerForm: {
		width: '100%',
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
	textFieldContainer: {
		flexGrow: 1,
		marginTop: token('space.negative.100'),
	},
	textField: {
		width: '100%',
		boxSizing: 'border-box',
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		paddingTop: token('space.050'),
		paddingRight: token('space.075'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
		border: `${token('border.width')} solid ${token('color.border.input')}`,
		borderRadius: token('radius.small'),
		outline: 'none',
		'&:focus': {
			borderColor: token('color.border.focused'),
			// @ts-expect-error - setting box shadow to match ADS
			boxShadow: `0 0 0 ${token('border.width')} ${token('color.border.focused')}`,
		},
	},
});

export const ChatListItem = ({
	id,
	name,
	elemBefore,
	description,
	elemAfter,
	isSelected,
	size,
	onSelect,
	onRename,
	onDelete,
	moreMenuItems,
	isMoreMenuOpen: isMoreMenuOpenProp,
	onMoreMenuOpenChange,
	renderContainer,
	afterContent,
	testId = 'chat-list-item',
	renameLabel: renameLabelProp,
	deleteLabel: deleteLabelProp,
	moreButtonLabel: moreButtonLabelProp,
	renameInputLabel: renameInputLabelProp,
	confirmRenameLabel: confirmRenameLabelProp,
	cancelRenameLabel: cancelRenameLabelProp,
}: ChatListItemProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [uncontrolledIsMoreMenuOpen, setUncontrolledIsMoreMenuOpen] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [localName, setLocalName] = useState(name);
	const [usingLocalName, setUsingLocalName] = useState(false);
	const [shouldRestoreFocusToMoreButton, setShouldRestoreFocusToMoreButton] = useState(false);

	const formRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const moreButtonRef = useRef<HTMLButtonElement | null>(null);
	const isMoreMenuOpen = isMoreMenuOpenProp ?? uncontrolledIsMoreMenuOpen;
	const wasMoreMenuOpenRef = useRef(isMoreMenuOpen);

	const setIsMoreMenuOpen = useCallback(
		(nextIsOpen: boolean) => {
			if (isMoreMenuOpenProp === undefined) {
				setUncontrolledIsMoreMenuOpen(nextIsOpen);
			}
			onMoreMenuOpenChange?.(nextIsOpen);
		},
		[isMoreMenuOpenProp, onMoreMenuOpenChange],
	);

	// ── Resolved labels ──────────────────────────────────────────────────

	const resolvedRenameLabel = renameLabelProp ?? formatMessage(defaultMessages.moreMenuRenameLabel);
	const resolvedDeleteLabel = deleteLabelProp ?? formatMessage(defaultMessages.moreMenuDeleteLabel);
	const resolvedMoreBtnLabel = moreButtonLabelProp ?? formatMessage(defaultMessages.moreBtnLabel);
	const resolvedRenameInputLabel =
		renameInputLabelProp ?? formatMessage(defaultMessages.renameInputLabel);
	const resolvedConfirmRenameLabel =
		confirmRenameLabelProp ?? formatMessage(defaultMessages.confirmRenameLabel);
	const resolvedCancelRenameLabel =
		cancelRenameLabelProp ?? formatMessage(defaultMessages.cancelRenameLabel);

	// ── More-menu handlers ───────────────────────────────────────────────

	const handleToggleMoreMenu = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			moreButtonRef.current = event.currentTarget;
			setIsMoreMenuOpen(!isMoreMenuOpen);
		},
		[isMoreMenuOpen, setIsMoreMenuOpen],
	);

	const handleCloseMoreMenu = useCallback(() => {
		setIsMoreMenuOpen(false);
	}, [setIsMoreMenuOpen]);

	useEffect(() => {
		const wasMoreMenuOpen = wasMoreMenuOpenRef.current;
		wasMoreMenuOpenRef.current = isMoreMenuOpen;

		/* eslint-disable-next-line @atlaskit/platform/no-direct-document-usage -- legacy onboarding baseline */
		if (wasMoreMenuOpen && !isMoreMenuOpen && document.activeElement === document.body) {
			moreButtonRef.current?.focus();
		}
	}, [isMoreMenuOpen]);

	const onRenameBtnClicked = useCallback(() => {
		setIsMoreMenuOpen(false);
		setLocalName(name);
		setIsEditing(true);
	}, [name, setIsMoreMenuOpen]);

	const onDeleteBtnClicked = useCallback(() => {
		setIsMoreMenuOpen(false);
		// Seed modal return-focus target with the originating "More" trigger.
		moreButtonRef.current?.focus();
		onDelete?.();
	}, [onDelete, setIsMoreMenuOpen]);

	// ── Rename handlers ──────────────────────────────────────────────────

	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const onSubmitRename = useCallback(
		async (value: string) => {
			const prevName = localName;
			setIsEditing(false);
			setLocalName(value);
			setUsingLocalName(true);
			const result = await onRename?.(value);
			if (!isMountedRef.current) {
				return;
			}
			if (result && 'error' in result) {
				// On error, revert and return authority to the parent prop immediately
				setLocalName(prevName);
				setUsingLocalName(false);
			}
		},
		[localName, onRename],
	);

	const onCancelRename = useCallback(() => {
		setShouldRestoreFocusToMoreButton(true);
		setIsEditing(false);
	}, []);

	// Sync external name prop changes → localName.
	// While usingLocalName is true (optimistic rename in flight), we trust our local value.
	// Once the parent prop catches up to our local value, we hand authority back to the parent.
	useEffect(() => {
		if (!usingLocalName) {
			setLocalName(name);
		} else if (name === localName) {
			// Parent confirmed the rename — hand authority back
			setUsingLocalName(false);
		}
	}, [name, usingLocalName, localName]);

	// Focus the input when editing starts
	useEffect(() => {
		if (!isEditing) {
			return;
		}
		inputRef.current?.focus();
	}, [isEditing]);

	// Restore focus to the more button after rename cancel
	useEffect(() => {
		if (isEditing || !shouldRestoreFocusToMoreButton) {
			return;
		}
		setShouldRestoreFocusToMoreButton(false);
		// Explicitly call .focus() — autoFocus alone is not reliable across all
		// IconButton implementations.
		moreButtonRef.current?.focus();
	}, [isEditing, shouldRestoreFocusToMoreButton]);

	// Close the rename form when the user clicks outside
	useEffect(() => {
		if (!isEditing) {
			return;
		}

		const handleClickOutside = (event: Event) => {
			if (formRef.current && !formRef.current.contains(event.target as Node)) {
				onCancelRename();
			}
		};

		const unbind = bind(window, {
			type: 'mousedown',
			listener: handleClickOutside,
			options: { capture: true },
		});

		return unbind;
	}, [isEditing, onCancelRename]);

	// ── Determine if more menu should show ───────────────────────────────

	const hasMoreMenu = Boolean(onRename || onDelete || moreMenuItems);

	// ── More-menu popup ──────────────────────────────────────────────────

	const moreMenu = hasMoreMenu ? (
		<Popup
			testId={`${testId}-more-popup`}
			isOpen={isMoreMenuOpen}
			onClose={handleCloseMoreMenu}
			zIndex={POPUP_Z_INDEX}
			content={() => (
				<MenuGroup>
					{onRename && (
						<ButtonItem
							testId={`${testId}-rename`}
							iconBefore={<EditIcon label="" />}
							onClick={onRenameBtnClicked}
						>
							{resolvedRenameLabel}
						</ButtonItem>
					)}
					{moreMenuItems}
					{onDelete && (
						<ButtonItem
							testId={`${testId}-delete`}
							iconBefore={<DeleteIcon label="" color={token('color.icon.danger')} />}
							onClick={onDeleteBtnClicked}
						>
							{resolvedDeleteLabel}
						</ButtonItem>
					)}
				</MenuGroup>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					autoFocus={shouldRestoreFocusToMoreButton}
					testId={`${testId}-more`}
					icon={() => <MoreIcon label="" size="small" />}
					appearance="subtle"
					spacing="compact"
					onClick={handleToggleMoreMenu}
					label={resolvedMoreBtnLabel}
				/>
			)}
			placement="bottom-end"
		/>
	) : null;

	// ── Rename form ──────────────────────────────────────────────────────

	const renameForm = (
		<div css={styles.renameForm} ref={formRef}>
			<Form
				onSubmit={(data: { chatName: string }) => {
					onSubmitRename(data.chatName);
				}}
				formProps={{
					class: styles.innerForm,
				}}
			>
				<Box xcss={styles.textFieldContainer}>
					<Field name="chatName" defaultValue={localName}>
						{({ fieldProps }) => (
							<input
								{...fieldProps}
								ref={inputRef}
								css={styles.textField}
								aria-label={resolvedRenameInputLabel}
								onKeyDown={(e: React.KeyboardEvent) => {
									if (e.key === 'Escape') {
										onCancelRename();
									}
								}}
								data-testid={`${testId}-name-edit`}
							/>
						)}
					</Field>
				</Box>
				<IconButton
					icon={(iconProps) => (
						<CheckMarkIcon {...iconProps} color={token('color.icon.accent.green')} />
					)}
					type="submit"
					appearance="subtle"
					shape="circle"
					spacing="compact"
					label={resolvedConfirmRenameLabel}
					testId={`${testId}-rename-confirm`}
				/>
				<IconButton
					icon={(iconProps) => <CrossIcon {...iconProps} color={token('color.icon.accent.red')} />}
					type="button"
					onClick={onCancelRename}
					appearance="subtle"
					shape="circle"
					spacing="compact"
					label={resolvedCancelRenameLabel}
					testId={`${testId}-rename-cancel`}
				/>
			</Form>
		</div>
	);

	// ── Render ────────────────────────────────────────────────────────────

	if (isEditing) {
		return renameForm;
	}

	const displayName = usingLocalName ? localName : name;

	return (
		<>
			{renderContainer({
				children: displayName,
				elemBefore,
				description,
				elemAfter,
				actionsOnHover: moreMenu ?? undefined,
				isSelected,
				onClick: onSelect,
				size,
				testId,
			})}
			{afterContent}
		</>
	);
};
